/* eslint-disable camelcase */
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import config from "../../utils/config"
import { withEnvironment } from "../extensions/with-environment"
import { withRootStore } from "../extensions/with-root-store"
import { getGeneralApiProblem } from "../../services/api/api-problem"
import { Alert } from "react-native"
import { AccessToken, LoginManager, Profile, GraphRequest, GraphRequestManager } from "../../components/fbsdk-next/fbsdk-next"
import { GoogleSignin, statusCodes } from "../../components/google-signin/google-signin"
import { appleAuth } from "../../components/apple-auth/apple-auth"
import { translate } from "../../i18n"
import { TokenModel } from "../token/token"
import moment from "moment"
import JalaAnalytic from "../../utils/jala-analytic"

const LoginStoreProps = {
  loading: types.optional(types.boolean, true),
  response: types.maybeNull(types.frozen()),
  error: types.maybeNull(types.frozen()),
  credentialStateForUser: types.maybeNull(types.number),
  user: types.maybeNull(types.frozen()),
  notRegisteredUser: types.maybeNull(types.frozen()),
  socialLoginEnabled: types.maybeNull(types.frozen()),
}

/**
 * Model description here for TypeScript hints.
 */
export const LoginModel = types
  .model("LoginStore")
  .props(LoginStoreProps)
  .extend(withEnvironment)
  .extend(withRootStore)
  .views((self) => ({})) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof LoginStoreProps, value: any) {
      if (self[key] === value) return
      self[key] = value as never;
    },
    init() {
      this.set('loading', false)
      this.set('error', {})
    },
    initSocialLoginEnabled() {
      self.axios.get('/settings', { params: { key__in: 'site.socialite' } })
        .then((response) => {
          try {
            this.set('socialLoginEnabled', JSON.parse(response.data.data[0]?.value || '{}'))
          } catch (error) {

          }
        })
    },
    async facebookSignIn() {
      const { BASE_URL } = config();
      const { tokenStore } = self.rootStore;

      const socialAuthentication = `${BASE_URL}/api/oauth/facebook`;
      try {
        await LoginManager.setLoginBehavior("web_only");
        const loginResult = await LoginManager.logInWithPermissions(['public_profile', 'email']);
        if (loginResult.grantedPermissions) {
          const accessToken = await AccessToken.getCurrentAccessToken();
          const body = { token: accessToken.accessToken };
          await self.apiSauce.post(socialAuthentication, body)
            .then((result) => {
              if (result.status === 200) {
                JalaAnalytic.log('Mobile app: facebook login count');
                JalaAnalytic.login({ method: 'facebook' });
                const { access_token, expires_at } = result.data as any;
                tokenStore.setToken(TokenModel.create({
                  access_token,
                  expires_in: moment.utc(expires_at).diff(moment(), 's')
                }))
                this.set('loading', false);
              } else {
                // __DEV__ && console.tron.log(result.status)
                // handle error not registered user
                const emailRequest = new GraphRequest(
                  '/me',
                  {
                    accessToken: accessToken.accessToken,
                    parameters: {
                      fields: {
                        string: 'email,name,first_name,middle_name,last_name',
                      },
                    },
                  },
                  (errorEmail, emailResponse) => {
                    if (!errorEmail) {
                      const email = emailResponse?.email
                      this.set('notRegisteredUser', {
                        email,
                        name,
                      })
                    }
                  },
                );

                // Start the graph request, which will call the callback after finished
                new GraphRequestManager().addRequest(emailRequest).start();
                this.set('error', getGeneralApiProblem(result))
                this.set('loading', false);
              }
              return result
            })
            .catch(async (e) => {
              const catched = { user: (await Profile.getCurrentProfile()), error: e };
              Alert.alert('Failed', '')
              LoginManager.logOut();
              this.set('error', catched)
              this.set('loading', false)
              return catched;
            });
        } else {
          Alert.alert(translate('AlertFailedTitle'), translate('AlertUserUnauthenticated'), [{ text: 'OK' }], { cancelable: false });
        }
      } catch (e) {
        Alert.alert(translate('AlertFailedTitle'), translate('AlertUserUnauthenticated'), [{ text: 'OK' }], { cancelable: false });
      }
    },
    async googleSignIn() {
      const { BASE_URL } = config();
      const { tokenStore } = self.rootStore;

      const socialAuthentication = `${BASE_URL}/api/oauth/google`;
      this.set('loading', true)
      if (!GoogleSignin.signIn) return;
      GoogleSignin.configure();
      try {
        await GoogleSignin.hasPlayServices();
        await GoogleSignin.signOut().catch()
        await GoogleSignin.signIn()
          .then(async (r) => {
            // if(r.status)
            const res = await GoogleSignin.getTokens();
            const body = { token: res.accessToken };
            return await self.apiSauce.post(socialAuthentication, body)
              .then((result) => {
                if (result.status === 200) {
                  JalaAnalytic.log('Mobile app: google login count');
                  JalaAnalytic.login({ method: 'google' });
                  const { access_token, expires_at } = result.data as any;
                  tokenStore.setToken(TokenModel.create({
                    access_token,
                    expires_in: moment.utc(expires_at).diff(moment(), 's')
                  }))
                  this.set('loading', false);
                } else {
                  // __DEV__ && console.tron.log(result.status)
                  this.set('error', getGeneralApiProblem(result))
                  this.set("notRegisteredUser", {email: r?.user?.email, name: r?.user?.name})
                  this.set('loading', false);
                }
                return result
              })
              .catch((e) => {
                const catched = { user: r, error: e };
                Alert.alert('Failed', '')
                GoogleSignin.signOut();
                this.set('error', catched)
                this.set('loading', false)
                return catched;
              });
        }).catch((e) => {
          GoogleSignin.signOut();
          throw e;
        });
      } catch (error) {
        if (error.code === statusCodes.SIGN_IN_CANCELLED) {
          // user cancelled the login flow
        } else if (error.code === statusCodes.IN_PROGRESS) {
          // operation (e.g. sign in) is in progress already
        } else if (error.code === statusCodes.PLAY_SERVICES_NOT_AVAILABLE) {
          // play services not available or outdated
        } else {
          // some other error happened
        }
        Alert.alert(translate('AlertFailedTitle'), translate('AlertUserUnauthenticated'), [{ text: 'OK' }], { cancelable: false });
      }
    },
    async fetchAndUpdateCredentialState () {
      if (self.user === null) {
        this.set('credentialStateForUser','N/A')
      } else {
        const credentialState = await appleAuth.getCredentialStateForUser(self.user);
        if (credentialState === appleAuth.State.AUTHORIZED) {
          this.set('credentialStateForUser','AUTHORIZED')
        } else {
          this.set('credentialStateForUser',credentialState)
        }
      }
    },
    async appleSignIn () {
      // start a login request
      if (!appleAuth.isSupported) {
        Alert.alert(translate('AlertFailedTitle'), translate('AppleSignInNotSupported'), [{ text: 'OK' }], { cancelable: false });
      }
      try {
        const appleAuthRequestResponse = await appleAuth.performRequest({
          requestedOperation: appleAuth.Operation.LOGIN,
          requestedScopes: [
            appleAuth.Scope.EMAIL,
            appleAuth.Scope.FULL_NAME,
          ],
        });
        const {
          user: newUser,
          // nonce,
          identityToken,
        } = appleAuthRequestResponse;
        const { BASE_URL } = config();
        const { tokenStore } = self.rootStore;

        this.set('user', newUser)
        this.fetchAndUpdateCredentialState()
          .catch(error => this.set('credentialStateForUser', `Error: ${error.code}`))
        if (identityToken) {
          // const { email } = jwt_decode(appleAuthRequestResponse.identityToken);
          // e.g. sign in with Firebase Auth using `nonce` & `identityToken`
          const socialAuthentication = `${BASE_URL}/api/oauth/${'apple'}`
          const body = { token: identityToken.toString() }
          return await self.apiSauce.post(socialAuthentication, body).then((response) => {
            if (response.status === 200) {
              JalaAnalytic.log('Mobile app: apple login count');
              JalaAnalytic.login({ method: 'apple' });
              const { access_token, expires_at } = response.data as any;
              tokenStore.setToken(TokenModel.create({
                access_token,
                expires_in: moment.utc(expires_at).diff(moment(), 's')
              }))
              this.set('loading', false);
            }
          }).catch(() => {
            // const isPrivate = email.toString().includes('privaterelay.appleid.com');
            // const emailUsed = isPrivate ? '' : email;
            // this.setState({ activeType: 'register', userInfo: { email: emailUsed } });
            Alert.alert(translate('AlertFailedTitle'), translate('AlertUserUnauthenticated'), [{ text: 'OK' }], { cancelable: false });
          });
        } else {
          // no token - failed sign-in
          Alert.alert(translate('AlertFailedTitle'), translate('AlertWrongInput'), [{ text: 'OK' }], { cancelable: false });
        }
      } catch (error) {
        if (error.code !== appleAuth.Error.CANCELED && appleAuth.isSupported) {
          // this.setState({ userInfo: this.user, activeType: 'register' });
          Alert.alert(translate('AlertFailedTitle'), translate('AlertUserUnauthenticated'), [{ text: 'OK' }], { cancelable: false });
        }
      }
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type LoginType = Instance<typeof LoginModel>
export interface Login extends LoginType {}
type LoginSnapshotType = SnapshotOut<typeof LoginModel>
export interface LoginSnapshot extends LoginSnapshotType {}
export const createLoginDefaultModel = () => types.optional(LoginModel, {})
